import {
  Component,
  OnInit,
  OnDestroy,
  EventEmitter,
  ViewChild,
  Output,
  Input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ViaviTableComponent, ViaviTabulatorModule } from '@nrwl/viavi-tabulator';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import {
  BenchmarkService,
  DialogService,
  FormatService,
  FusionTransformationService,
  IBatchActionState,
  ITableObject,
  LoggerService,
  NotificationService,
  TEST_STATE,
  TabulatorService,
  TestListEntity,
  TestListService,
  UserAuth,
  ValuesExchangeProvider,
  TestTypeValue,
  CommonLibModule
} from '@nrwl/common-lib';
import { TwampReflectorResultsComponent } from '../twamp-reflector-results/twamp-reflector-results.component';
import { Options } from 'tabulator-tables';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { assign, get, isEmpty, map } from 'lodash';
import { takeUntil } from 'rxjs/operators';
import { CommonMaterialModule } from '@nrwl/common-material';
import { MatDialog } from '@angular/material/dialog';

export interface IParams {
  action: string;
  data: Array<string>;
  rowData?: any;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'test-list-table',
  standalone: true,
  imports: [CommonModule, ViaviTabulatorModule, CommonMaterialModule, CommonLibModule],
  templateUrl: './test-list-table.component.html',
  styleUrls: ['./test-list-table.component.scss']
})
export class TestListTableComponent implements OnInit, OnDestroy {
  @ViewChild(ViaviTableComponent) child: ViaviTableComponent;
  @Output() hasData: EventEmitter<boolean> = new EventEmitter(false);
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() data: any;
  @Input() refreshTable?: BehaviorSubject<boolean>;
  @Input() layeredSource: string | any[];
  @Input() isFilteredApplied: any;
  @Input() columnHeader: string | string[];
  @Input() dynamicColumn: any;
  @Input() tableId?: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() private widgetData: any;
  onTableFiltered: (click) => void;
  selectedRowIndex: BehaviorSubject<number>;
  showPaginator: BehaviorSubject<boolean>;
  resetCheckbox: Subject<string[]>;
  testListData: ITableObject;
  prevTestlistData: ITableObject;
  dataSourceSubscription: Subscription;
  sourceInfo = 'Test list';
  def: {
    managerUrl: string;
    historical: boolean;
    widgetOptions: {
      hideActionsColumn: boolean;
      id: string;
    };
    widgetType: 'table';
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    actions: any;
  };

  // eslint-disable-next-line @typescript-eslint/ban-types
  onTableRowClick: Function;
  isLoading: boolean;
  batchActionState: IBatchActionState;
  dataSource: BehaviorSubject<ITableObject>;
  hasManageTest = false;
  showLog = false;
  table: Options;
  TEST_LIMIT_COUNT = 2000;
  private readonly unsubscribe: Subject<void> = new Subject();
  private startStates: Array<string> = ['starting', 'queued', 'running', 'scheduled'];
  private stopStates: Array<string> = [
    'stopping',
    'stopped',
    'failed',
    'finished',
    'cancelled',
    'aborted'
  ];
  errorMessage: any;
  errorDescription: any;
  requestFailed: boolean;
  testStatusCounters = [
    {
      id: 'all',
      label: 'Found',
      count: 0,
      selected: true,
      class: ''
    }
  ];
  selectedFilterItem = 'all';
  constructor(
    private readonly testListService: TestListService,
    private readonly transformationService: FusionTransformationService,
    private readonly logger: LoggerService,
    private readonly dialogService: DialogService,
    private readonly userAuth: UserAuth,
    private readonly exchange: ValuesExchangeProvider,
    private readonly formatService: FormatService,
    private readonly noticeService: NotificationService,
    private readonly navigator: NavigationProvider,
    private readonly tabulatorService: TabulatorService,
    private benchmarkService: BenchmarkService,
    private readonly dialog: MatDialog
  ) {
    this.onTableFiltered = null;
    this.dataSource = new BehaviorSubject<ITableObject>(null);
    this.showPaginator = new BehaviorSubject<boolean>(true);
    this.selectedRowIndex = new BehaviorSubject<number>(null);
    this.resetCheckbox = new Subject<string[]>();

    this.testListData = null;
    this.prevTestlistData = null;
    this.onTableRowClick = null;
    this.isLoading = false;
    this.batchActionState = null;
    this.table = this.tabulatorService.commonTableConfiguration();
    this.table.height = window.innerHeight - 243 + 'px';
    this.table.groupHeader = this.tabulatorService.groupHeader;
    this.table.rowHeight = 55;
  }

  ngOnInit(): void {
    this.table.persistenceID = this.tableId ?? this.table.persistenceID;
    if (this.layeredSource?.length > 0) {
      this.table.persistenceID =
        this.layeredSource[0] === 'Device' ? this.layeredSource[0] : this.table.persistenceID;
    }
    this.exchange.notifyListeners('new_test', { noValue: true });
    this.exchange.registerInterest('new_test', this.updateCache.bind(this));
    this.testListService.clearQueues();
    assign(this, this.widgetData);

    this.onTableRowClick = (data: { tests: { type: any }; id: any; runOnce: boolean }) => {
      const testsLists = [data.tests];
      const testStatus = this.testListService.getTestListById(data.id)?.getTestStatus();
      if (testsLists.length === 0 && isEmpty(testsLists)) {
        this.logger.debug(`Test has no tests. Click ignored`);
        return;
      }
      if (testStatus !== TEST_STATE.READY) {
        this.drillDownTestDetailsPage(data.tests.type, data.id, data.runOnce);
      }
    };

    this.dataSourceSubscription = this.data.subscribe((data: TestListEntity[]) => {
      this.benchmarkService.registerPageEvent('tableDataTransform');
      this.assignData(data);
      this.table.initialSort = this.widgetData.def.widgetOptions.defaultSorting;
      this.table.columns = this.testListData.column;
      this.table.data = this.testListData.data;
      this.table = Object.assign({}, this.table);
      this.getCounterItems();
      this.benchmarkService.logPageEventTime(
        'Testlist table transforming data',
        'tableDataTransform'
      );
    });
    this.onTableFiltered = filter => {
      if (filter) {
        const data = this.child?.table?.getHeaderFilters().length
          ? this.child.filteredItems()
          : this.testListData.data;
        if (!isEmpty(data)) {
          this.testStatusCounters.forEach(item => {
            if (item.id === 'all') {
              item.count = data.length;
            } else {
              item.count = data.filter(test => test.state.label.toLowerCase() === item.id).length;
            }
          });
        }
      }
    };

    this.dataSourceSubscription = this.refreshTable.subscribe(refresh => {
      if (refresh) {
        this.refreshTestList();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.dataSourceSubscription) {
      this.dataSourceSubscription.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  assignData(data: Array<TestListEntity>): void {
    this.testListData = this.transformationService.transformData(
      'test-list-data',
      data,
      false,
      this.dynamicColumn
    );
    this.updateColumn();
    this.hasManageTest = this.userAuth.isAuthedFor('MANAGE_TESTS');
    if (!this.hasManageTest) {
      this.testListData.column.pop();
      this.def.widgetOptions.hideActionsColumn = true;
    }

    this.dataSource.next(this.testListData);
    this.hasData.emit(!isEmpty(data));
  }

  getCounterItems(): void {
    this.testStatusCounters = [
      {
        id: 'all',
        label: 'All',
        count: 0,
        selected: true,
        class: ''
      }
    ];

    if (this.testListData?.data?.length) {
      this.testListData?.data.forEach(item => {
        const stateLabel = item.state.label;
        const existingEntry = this.testStatusCounters.find(
          entry => entry.id === stateLabel.toLowerCase()
        );
        if (existingEntry) {
          existingEntry.count++;
        } else {
          this.testStatusCounters.push({
            id: stateLabel.toLowerCase(),
            label: stateLabel,
            count: 1,
            selected: false,
            class: ''
          });
        }
      });
      this.testStatusCounters[0].count = this.testListData.data.length;
    }
    const selectedItem = this.testStatusCounters.find(item => item.id === this.selectedFilterItem);
    this.counterSelected(selectedItem);
  }

  //eslint-disable-next-line
  counterSelected(item: any): void {
    this.selectedFilterItem = item.id;
    let fiteredData = [];
    if (this.testListData.data?.length) {
      if (this.selectedFilterItem === 'all') {
        fiteredData = this.testListData.data;
      } else {
        fiteredData = this.testListData.data.filter(
          test => test.state.label.toLowerCase() === this.selectedFilterItem
        );
      }
    }
    this.table.data = fiteredData;
    this.table = Object.assign({}, this.table);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  drillDownTestDetailsPage(testType: any, testId: any, runOnce: boolean): void {
    switch (testType) {
      case TestTypeValue.SERVICE_CONTINUITY: {
        this.goToApplicationSummary(testId);
        break;
      }
      case TestTypeValue.VOIP: {
        this.goToVoipResultsSummary(testId);
        break;
      }
      case TestTypeValue.RFC2544:
      case TestTypeValue.TWAMP:
      case TestTypeValue.Y1731:
      case TestTypeValue.BERT: {
        const routeId = 'test-running';
        const testStatus = this.testListService.getTestListById(testId).getTestStatus();
        if (testStatus === TEST_STATE.FINISHED) {
          this.goToFinalReports(testId);
        } else {
          this.goToActiveTestDetailsPage(testId, routeId);
        }
        break;
      }
      case TestTypeValue.Y1564: {
        const testStatus = this.testListService.getTestListById(testId).getTestStatus();
        if (testStatus === TEST_STATE.FINISHED) {
          this.goToFinalReports(testId);
        } else if (testStatus === TEST_STATE.RUNNING || runOnce) {
          this.goToActiveTestDetailsPage(testId, 'test-running');
        } else if (!runOnce) {
          this.goToActiveTestDetailsPage(testId, 'y1564-scheduled-page');
        }
        break;
      }
      case TestTypeValue.UDP: {
        const routeId = 'test-running';
        const testStatus = this.testListService.getTestListById(testId).getTestStatus();
        if (testStatus === TEST_STATE.FINISHED) {
          this.goToFinalReports(testId);
        } else {
          this.goToActiveTestDetailsPage(testId, routeId);
        }
        break;
      }
      case TestTypeValue.TRUESPEED: {
        const testStatus = this.testListService.getTestListById(testId).getTestStatus();
        if (testStatus === TEST_STATE.FINISHED) {
          this.goToFinalReports(testId);
        } else if (testStatus === TEST_STATE.RUNNING || runOnce) {
          this.goToActiveTestDetailsPage(testId, 'test-running');
        } else if (!runOnce) {
          this.goToActiveTestDetailsPage(testId, 'truespeed-page');
        }
        break;
      }
      case TestTypeValue.MULTISTEP:
        this.goToMultiStepSummary(testId);
        break;
      case TestTypeValue.TWAMP_REFLECTOR:
      case TestTypeValue.TWAMP_LIGHT_REFLECTOR:
        this.openMonitorReflector(testId);
        break;
      case TestTypeValue.HTTP:
      case TestTypeValue.FTP: {
        const routeId = 'test-running';
        const testStatus = this.testListService.getTestListById(testId).getTestStatus();
        if (testStatus === TEST_STATE.FINISHED) {
          this.goToFinalReports(testId);
        } else {
          this.goToActiveTestDetailsPage(testId, routeId);
        }
        break;
      }
    }
  }

  goToApplicationSummary(testId: string): void {
    this.navigator.navigateToDashboard(['application-summary'], {
      queryParams: {
        appId: null,
        appName: null,
        testId,
        source: this.sourceInfo,
        layeredSource: this.layeredSource,
        isFilteredApplied: this.isFilteredApplied,
        queryParamsHandling: 'merge',
        replaceUrl: true
      }
    });
  }

  goToVoipResultsSummary(testId: string): void {
    this.navigator.navigateToDashboard(['voip-summary'], {
      queryParams: {
        appId: null,
        appName: null,
        testId,
        source: this.sourceInfo,
        layeredSource: this.layeredSource,
        isFilteredApplied: this.isFilteredApplied,
        queryParamsHandling: 'merge',
        replaceUrl: true
      }
    });
  }

  goToMultiStepSummary(testId: string): void {
    this.navigator.navigateToDashboard(['multi-step-summary'], {
      queryParams: {
        appId: null,
        appName: null,
        testId,
        source: this.sourceInfo,
        layeredSource: this.layeredSource,
        isFilteredApplied: this.isFilteredApplied,
        queryParamsHandling: 'merge',
        replaceUrl: true
      }
    });
  }
  goToActiveTestDetailsPage(testId: string, type: string): void {
    this.navigator.navigateToDashboard([type], {
      queryParams: {
        testId,
        testName: this.testListService.getTestListById(testId).getName(),
        testType: this.testListService.getTestListById(testId).getTestType(),
        source: this.sourceInfo,
        layeredSource: this.layeredSource,
        isFilteredApplied: this.isFilteredApplied,
        queryParamsHandling: 'merge',
        replaceUrl: true
      }
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  changePerpective(pageId: any, testId: any): void {
    // pass empty breadcrumb label value to override previous value in url
    this.navigator
      .addRootParam('activetest_clicked', {})
      .navigateToPage([pageId, testId], { queryParams: { testId: testId } });
  }

  isTableWidget(): boolean {
    return get(this, 'def.widgetType') === 'table';
  }

  goToFinalReports(testId: string): void {
    const testType = this.testListService.getTestListById(testId).getTestType().toLowerCase();
    const testName = this.testListService.getTestListById(testId).getName();
    this.navigator.navigateToDashboard(['reports', testType, testId], {
      queryParams: {
        testId,
        source: this.sourceInfo,
        layeredSource: this.layeredSource,
        testName: testName,
        queryParamsHandling: 'merge',
        replaceUrl: true,
        runId: this.testListService.getTestListById(testId).getRunId(),
        testStatus: this.testListService.getTestListById(testId).getTestStatus()
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  sendAction(params: any): void {
    const items = params.data || this.getSelectedItemsIds();
    let testId;
    if (items.length > 0) {
      switch (params.action) {
        case 'view_reports':
          testId = items.join();
          this.goToFinalReports(testId);
          break;
        case 'clone':
        case 'edit':
        case 'view':
        case 'logs': {
          this.runAction(this, {
            action: params.action,
            items: items
          });
          break;
        }
        case 'start': {
          this.startTestAction(params, items);
          break;
        }
        case 'stop': {
          this.stopTestAction(params, items);
          break;
        }
        case 'remove': {
          this.removeTestAction(params, items);
          break;
        }
        case 'monitor_reflector': {
          //  this.openMonitorReflector(items);
          break;
        }
        default: {
          const param = {
            id: params.action + '.testlist',
            callBackService: this,
            callBackFnParams: {
              action: params.action,
              items: items
            },
            callBackFuntion: this.runAction
          };
          this.dialogService.showDialog('confirm', 'body', param);
          break;
        }
      }
    }
  }

  get isActionsDisabled(): boolean {
    return this.getSelectedItemsIds().length === 0;
  }

  // eslint-disable-next-line
  openMonitorReflector(testId: string) {
    const selectedTestData: TestListEntity = this.testListService.getTestListById(testId);
    const originatorDevice = selectedTestData.getOriginatorAgent();
    if (selectedTestData.getTestStatus() === TEST_STATE.RUNNING && originatorDevice) {
      this.dialog.open(TwampReflectorResultsComponent, {
        width: '74rem',
        disableClose: true,
        data: {
          deviceObj: originatorDevice,
          selectedTestID: testId,
          testName: selectedTestData.getName(),
          testType: selectedTestData.getTestType()
        }
      });
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onRowActionClick(actionItem: any): void {
    const actionParam = this.getURLBasedOnActions(actionItem.label);
    actionParam.data = [actionItem.id];
    actionParam.rowData = actionItem.rowData;
    this.sendAction(actionParam);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getURLBasedOnActions(actions: any): any {
    let result = {};
    switch (actions) {
      case 'Remove': {
        result = {
          action: 'remove'
        };
        break;
      }
      case 'Start': {
        result = {
          action: 'start'
        };
        break;
      }
      case 'Stop': {
        result = {
          action: 'stop'
        };
        break;
      }
      case 'Edit': {
        result = {
          action: 'edit'
        };
        break;
      }
      case 'Clone': {
        result = {
          action: 'clone'
        };
        break;
      }
      case 'Status': {
        result = {
          action: 'logs'
        };
        break;
      }
      case 'View': {
        result = {
          action: 'view'
        };
        break;
      }
      case 'View_reports': {
        result = {
          action: 'view_reports'
        };
        break;
      }
      case 'Monitor_Reflector': {
        result = {
          action: 'monitor_reflector'
        };
        break;
      }
    }
    return result;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  editOrCloneTest(params: any): void {
    const testId = params.items.join();
    const selectedTestData: TestListEntity = this.testListService.getTestListById(testId);
    this.testListService.editTest(selectedTestData, params.action);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  showLogs(params: any): void {
    const testId = params.items.join();
    const testName = this.testListService.getTestListById(testId).getName();
    const source = this.layeredSource;
    this.navigator.navigateToDashboard(['test-logs'], {
      queryParams: {
        testId,
        testName,
        source
      }
    });
  }

  // eslint-disable-next-line
  drillDownPage(params: any): void {
    const testId = params.items.join();
    const testType = this.testListService.getTestListById(testId).getTestType();
    const runOnce = this.testListService.getTestListById(testId).isRunOnceTest();
    if (
      testType === TestTypeValue.TWAMP_REFLECTOR ||
      testType === TestTypeValue.TWAMP_LIGHT_REFLECTOR
    ) {
      this.openMonitorReflector(testId);
    } else {
      this.drillDownTestDetailsPage(testType, testId, runOnce);
    }
  }

  refreshTestList(): void {
    this.isLoading = true;
    this.testListService
      .updateTestLists(this.testListService.getTestLists())
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        }
      );
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  removeTest(service: any, params: any): void {
    this.isLoading = true;
    const data = { schedules: params.items };
    service.testListService
      .removeTest$(data)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          service.testListService.removeTestsResponse(response, params.items);
          this.isLoading = false;
          this.resetCheckbox.next(params.items);
          let succesMsg = 'Your test has been removed.';
          if (params.items.length > 1) {
            succesMsg = 'Your tests have been removed.';
          }
          this.noticeService.onSuccess(succesMsg);
        },
        error => {
          this.isLoading = false;
          this.noticeService.onFail(error.error.data.message);
        },
        () => {}
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  stopTest(params: any): void {
    this.isLoading = true;
    let bulkPayload = [];
    bulkPayload = this.child?.selection.map(x => {
      return {
        action: 'stop',
        'schedule-id': x.id,
        'schedule-name': x.name.label,
        'agent-id': x.location.label
      };
    });
    if (params.rowData) {
      bulkPayload = [
        {
          action: 'stop',
          'schedule-id': params.rowData.id,
          'schedule-name': params.rowData.name?.label,
          'agent-id': params.rowData.location?.label
        }
      ];
    }
    this.testListService
      .stopTest$(bulkPayload)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
          this.testListService.updateTestStopResponse(response, params.items);
          this.resetCheckbox.next(params.items);
        },
        error => {
          this.isLoading = false;
          this.updateCache({ noValue: false });
          this.noticeService.onFail(error.message);
        },
        () => {}
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  startTest(params: any): void {
    this.isLoading = true;
    let bulkPayload = [];
    bulkPayload = this.child?.selection.map(x => {
      return {
        'schedule-id': x.id,
        'schedule-name': x.name.label,
        'agent-id': x.location.label
      };
    });
    if (params.rowData) {
      bulkPayload = [
        {
          'schedule-id': params.rowData.id,
          'schedule-name': params.rowData.name?.label,
          'agent-id': params.rowData.location?.label
        }
      ];
    }
    this.testListService
      .startTest$(bulkPayload)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
          this.testListService.updateTestStartResponse(response, params.items);
          this.resetCheckbox.next(params.items);
        },
        error => {
          this.isLoading = false;
          this.updateCache({ noValue: false });
          this.noticeService.onFail(error.message);
        },
        () => {}
      );
  }

  generateBatchActionState(): void {
    let batchActionState = {
      stopped: false,
      started: false,
      readyState: false,
      agentDestroyed: false,
      disable: false,
      enable: false,
      destroy: true
    };
    const selectedIds = this.child.selection;
    const selectedTests = [];
    selectedIds.forEach(testId => {
      const test = this.testListService
        .getTestLists()
        .find(test => test.testListData.id == testId.id);
      selectedTests.push(test);
    });

    selectedTests.forEach(propValue => {
      batchActionState = this.populateActionState(propValue, batchActionState);
    });
    this.batchActionState = batchActionState;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  getArrayString(array: Array<Object>, key: string): string {
    return map(
      array.filter(item => {
        return item[key];
      }),
      key
    ).join(', ');
  }

  populateActionState(item: TestListEntity, actionState: IBatchActionState): IBatchActionState {
    const state = item.getTestStatus();
    if (state) {
      if (state === 'ready') {
        actionState.readyState = true;
      }
      if (this.startStates.includes(state) && !actionState.started) {
        actionState.started = true;
      } else if (this.stopStates.includes(state) && !actionState.stopped) {
        actionState.stopped = true;
      }
    }
    return actionState;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get itemActions(): any {
    return get(this.def, 'actions.item', {});
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get bulkActions(): any {
    return get(this.def, 'actions.bulk', {});
  }

  isDataAvailable(): boolean {
    return get(this.testListData, 'data.length') > 0;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  updateCache(data: any): void {
    if (data && !data.noValue) {
      this.testListService.updateTestResponse();
    }
  }
  // eslint-disable-next-line
  startTestAction(params: any, items): void {
    const singleTestTitle = params.rowData?.name
      ? 'Start Test - ' + params.rowData?.name?.label
      : 'Start Test';
    if (items.length > this.TEST_LIMIT_COUNT) {
      this.showBulkTestLimitDialog('start');
    } else {
      const paramStart = {
        id: params.action + '.bulk.testlist',
        callBackService: this,
        alternateTitle:
          items.length > 1
            ? 'Start ' + this.formatService.formatNumberWithCommas(items.length) + ' tests?'
            : singleTestTitle,
        alternateMsg:
          items.length > 1
            ? 'This action will start ' +
              this.formatService.formatNumberWithCommas(items.length) +
              ' tests. Would you like to continue?'
            : '',
        callBackFnParams: {
          action: params.action,
          items: items,
          rowData: params.rowData
        },
        callBackFuntion: this.runAction
      };
      this.dialogService.showDialog('confirm', 'body', paramStart);
    }
  }
  // eslint-disable-next-line
  stopTestAction(params: any, items): void {
    const singleTestTitle = params.rowData?.name
      ? 'Stop Test - ' + params.rowData?.name?.label
      : 'Stop Test';
    if (items.length > this.TEST_LIMIT_COUNT) {
      this.showBulkTestLimitDialog('stop');
    } else {
      const param = {
        id: params.action + '.testlist',
        callBackService: this,
        alternateTitle:
          items.length > 1
            ? 'Stop ' + this.formatService.formatNumberWithCommas(items.length) + ' tests?'
            : singleTestTitle,
        alternateMsg:
          items.length > 1
            ? 'Stopping these tests means data for them will no longer be collected. Would you like to continue?'
            : '',
        callBackFnParams: {
          action: params.action,
          items: items,
          rowData: params.rowData
        },
        callBackFuntion: this.runAction
      };
      this.dialogService.showDialog('confirm', 'body', param);
    }
  }
  // eslint-disable-next-line
  removeTestAction(params: IParams, items): void {
    const singleTestTitle = params.rowData?.name
      ? 'Remove Test - ' + params.rowData?.name?.label
      : 'Remove Test';
    if (items.length > this.TEST_LIMIT_COUNT) {
      this.showBulkTestLimitDialog('remove');
    } else {
      const param = {
        id: params.action + '.testlist',
        callBackService: this,
        alternateTitle:
          items.length > 1
            ? 'Remove ' + this.formatService.formatNumberWithCommas(items.length) + ' tests?'
            : singleTestTitle,
        alternateMsg:
          items.length > 1
            ? 'Removing these tests will also delete any data that has been collected. Would you like to continue?'
            : '',
        callBackFnParams: {
          action: params.action,
          items: items
        },
        callBackFuntion: this.runAction
      };
      this.dialogService.showDialog('confirm', 'body', param);
    }
  }
  showBulkTestLimitDialog(action: string): void {
    const param = {
      id: 'bulk.selection.testlist',
      alternateMsg: `You can only ${action} ${this.TEST_LIMIT_COUNT} items at a time. Please reduce your amount and try again.`,
      alternateTitle: `Cannot ${action} tests`,
      callBackService: this
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }

  private getSelectedItemsIds(): string[] {
    return this.child?.selection.map(x => x.id) || [];
  }

  private runAction(service, params): void {
    const action = params.action;
    switch (action) {
      case 'start': {
        service.startTest(params);
        break;
      }
      case 'edit':
      case 'clone': {
        service.editOrCloneTest(params);
        break;
      }
      case 'remove': {
        service.removeTest(service, params);
        break;
      }
      case 'stop': {
        service.stopTest(params);
        break;
      }
      case 'logs': {
        service.showLogs(params);
        break;
      }
      case 'view': {
        service.drillDownPage(params);
        break;
      }
    }
    service.child.selection = [];
  }
  private updateColumn(): void {
    if (this.columnHeader) {
      this.testListData.column.forEach(item => {
        if (this.columnHeader.indexOf(item.tabulatorInfo.title) === -1) {
          item.tabulatorInfo.visible = false;
        }
      });
    }
    if (this.dynamicColumn) {
      this.testListData.column = [...this.testListData.column, ...this.dynamicColumn];
    }
    if (this.columnHeader && this.columnHeader.indexOf('Actions') !== -1) {
      this.testListData.column.push(
        this.testListData.column.splice(
          this.testListData.column.findIndex(v => v.tabulatorInfo.field == 'actions'),
          1
        )[0]
      );
    }
  }
}
